import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import { CaretRight } from '@liveauctioneers/hammer-ui-core/icons';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link, SubtleLink, WeakLink } from '@liveauctioneers/hammer-ui-core/link';
import { Caption, H3, LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import useVisibilitySensor from '@rooks/use-visibility-sensor';
import { MouseEvent, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { trackAdUnitClick, trackAdUnitVisible } from '@/redux/modules/analytics';
import { getCatalogAd } from '@/redux/modules/catalog/ad/catalogAd.selectors';
import { CatalogAds } from '@/redux/modules/catalog/ad/catalogAd.types';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import {
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { AmpProduct, useTrackAmpClicked, useTrackAmpSeen } from '@/utils/amplitude/analytics';
import { addImageParams, AddImageParamTypes, getCatalogUrl, getSellerUrl } from '@/utils/urls';
import useSSRFriendlyDate from '@/utils/useSSRFriendlyDate';

const imageParams: AddImageParamTypes = {
    height: 375,
    width: 375,
};

export const SearchResultsAd = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { formatMessage } = useIntl();
    const isMobile = useIsMobileLayout();
    const adRef = useRef<HTMLAnchorElement>(null);
    const { isVisible } = useVisibilitySensor(adRef, {
        intervalCheck: false,
        partialVisibility: true,
        resizeCheck: true,
        scrollCheck: true,
    });
    const previousAdVisible = usePrevious(isVisible);

    const trackAmpClicked = useTrackAmpClicked();
    const trackAmpSeen = useTrackAmpSeen();

    const { adUnitId, catalogId, consignment, imageUrl1, imageUrl2, imageUrl3, saleStartTs } = useAppSelector((state) =>
        getCatalogAd(state, { pageName: CatalogAds.SearchPageBanner })
    );

    const catalogTitle = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));

    const clientSaleStartTs = useSSRFriendlyDate(saleStartTs);

    const catalogUrl = getCatalogUrl(catalogId, catalogTitle);

    const trackAndNavigate = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        dispatch(
            trackAdUnitClick({
                adUnitId,
                consignment: Boolean(consignment),
                page: 'Search Page Banner',
            })
        );
        trackAmpClicked({ ampProduct: AmpProduct.SearchPageBanner, catalogId, houseId: sellerId });
        navigate(catalogUrl);
    };
    const images = useMemo(() => {
        return [imageUrl1, imageUrl2, imageUrl3].filter((image) => Boolean(image));
    }, [imageUrl1, imageUrl2, imageUrl3]);

    useEffect(() => {
        if (catalogId) {
            dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] }));
        }
    }, [catalogId, dispatch]);

    useEffect(() => {
        if (!previousAdVisible && isVisible && Boolean(adUnitId)) {
            dispatch(
                trackAdUnitVisible({
                    adUnitId,
                    page: 'Search Page Banner',
                })
            );
            trackAmpSeen({ ampProduct: AmpProduct.SearchPageBanner, catalogId, houseId: sellerId });
        }
    }, [adUnitId, catalogId, consignment, dispatch, isVisible, previousAdVisible, sellerId, trackAmpSeen]);

    if (!catalogId) {
        return null;
    }

    return (
        <section
            className="ax-w-[1200px] mx-auto mt-[40px]"
            data-testid="searchResultsAdUnit"
            ref={adRef}
        >
            <section
                className={cn(
                    'space-between flex',
                    'rounded-md border border-border-secondary',
                    'bg-background-tertiary',
                    isMobile ? 'flex-col' : 'flex-row'
                )}
            >
                <section
                    className={cn(
                        isMobile ? '' : 'max-w-1/2 w-1/2',
                        'p-xs',
                        'flex items-center justify-center gap-xs',
                        isMobile ? 'rounded-t-md' : 'rounded-l-md',
                        isMobile ? 'border-b border-border-tertiary' : 'border-r border-border-tertiary',
                        'bg-background-primary'
                    )}
                >
                    {images.map((imageUrl) => (
                        <Link
                            href={catalogUrl}
                            key={`${imageUrl}-catalog-banner-ad-image`}
                            onClick={(e) => trackAndNavigate(e)}
                        >
                            <img
                                className={cn(
                                    isMobile ? 'max-h-[234px] max-w-[234px]' : 'max-h-[189px] max-w-[189px]',
                                    'h-auto w-full'
                                )}
                                height={String(imageParams.height)}
                                src={addImageParams({ ...imageParams, url: imageUrl })}
                                width={String(imageParams.width)}
                            />
                        </Link>
                    ))}
                </section>
                <section
                    className={cn(
                        isMobile ? '' : 'max-w-1/2 w-1/2',
                        isMobile ? 'px-sm py-md' : 'px-md py-sm',
                        'flex flex-col items-center justify-center',
                        'rounded-r-md',
                        'bg-background-tertiary'
                    )}
                >
                    <LabelMini className="mb-[2px]">{clientSaleStartTs}</LabelMini>
                    <H3 className="mb-[8px] max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-center">
                        {catalogTitle}
                    </H3>
                    <SubtleLink
                        className="flex items-center"
                        href={catalogUrl}
                        onClick={(e) => trackAndNavigate(e)}
                        size={isMobile ? 'sm' : 'md'}
                    >
                        {formatMessage(
                            { id: 'searchPage.searchResultsAd.exploreAuction' },
                            {
                                icon: (
                                    <CaretRight
                                        key="search-results-ad-explore-icon"
                                        size={isMobile ? 'xs' : 'sm'}
                                    />
                                ),
                            }
                        )}
                    </SubtleLink>
                </section>
            </section>
            <section className="justify-self-end">
                <Caption color="secondary">
                    {formatMessage(
                        { id: 'searchPage.searchResultsAd.sponsoredBy' },
                        {
                            houseLink: (
                                <WeakLink
                                    key="search-results-ad-sponsored-house-link"
                                    href={getSellerUrl(sellerId, sellerName)}
                                    size="xs"
                                >
                                    {sellerName}
                                </WeakLink>
                            ),
                        }
                    )}
                </Caption>
            </section>
        </section>
    );
};
